import { lights, normalize } from 'three/tsl';
import * as THREE from 'three/webgpu';
import { Atmosphere } from '../../engine/atmosphere/Atmosphere';
import type { AtmosphereDef } from '../../engine/atmosphere/AtmosphereDef';
import { type CloudDef, CloudLayer } from '../../engine/atmosphere/CloudLayer';
import type { AmbienceDef } from '../../engine/audio/Ambience';
import type { PlayerController } from '../../engine/character/PlayerController';
import { PLAYER_CENTER_HEIGHT } from '../../engine/character/PlayerController';
import { flags } from '../../engine/core/flags';
import { quality } from '../../engine/core/settings';
import { type BirdDef, Birds } from '../../engine/fauna/Birds';
import { GravityField } from '../../engine/gravity/GravityField';
import { type GlowDef, LightRig, type VeilDef } from '../../engine/lighting/LightRig';
import type { PhysicsWorld } from '../../engine/physics/PhysicsWorld';
import { createSunMaterial } from '../../engine/render/materials/sandboxMaterials';
import type { RenderOrigin } from '../../engine/render/RenderOrigin';
import { BiomeField, type BiomeFieldDef } from '../../engine/terrain/BiomeField';
import { TerrainCollider } from '../../engine/terrain/TerrainCollider';
import type { TerrainMouth } from '../../engine/terrain/TerrainMouth';
import { heightBound, type TerrainProfile } from '../../engine/terrain/TerrainProfile';
import { TerrainQuadtree, type TNode } from '../../engine/terrain/TerrainQuadtree';
import { TerrainRenderer } from '../../engine/terrain/TerrainRenderer';
import {
  type RootCoord,
  SphereShape,
  type TerrainShape,
  TorusShape,
} from '../../engine/terrain/TerrainShape';
import type { TerrainStyle } from '../../engine/terrain/TerrainStyle';
import { bakeable, TILE_RES, TileGenerator } from '../../engine/terrain/TileGenerator';
import { WorldPanorama } from '../../engine/terrain/WorldPanorama';
import { type HollowDef, makeHollow, type SunDef } from '../../engine/universe/HollowDef';
import { CanopyShadow } from '../../engine/vegetation/CanopyShadow';
import { GrassRenderer } from '../../engine/vegetation/GrassRenderer';
import { type MoteDef, Motes } from '../../engine/vegetation/Motes';
import { TreeRenderer } from '../../engine/vegetation/TreeRenderer';
import type { GrassStyle, TreeStyle } from '../../engine/vegetation/VegetationStyle';
import { terrainDetail } from '../hollows/detail';
import type { GameWorld } from './GameWorld';

type RGB = [number, number, number];

/** Everything that makes one terrain hollow different from another. */
export interface HollowSceneDef {
  id: string;
  name: string;
  center: RGB;
  /** The sphere's radius, or for a torus the radius of the circle through its tube's middle. */
  radius: number;
  /**
   * Make the hollow a torus (a donut, its ring around the y axis) with this tube radius.
   * Tori have no sun, air or caves yet: light them with `glow`.
   */
  torus?: { tube: number };
  seed: number;
  /** Surface gravity (m/s²; default: by size, see defaultSurfaceGravity). */
  gravity?: number;
  sun: SunDef | null;
  terrain: TerrainProfile;
  /**
   * The air, or null for none. Tiny hollows have no haze worth drawing, and hollows without
   * air share one post pipeline (and its shaders and render targets) in a cave world.
   */
  air: AtmosphereDef | null;
  /** Clouds need air. */
  clouds: CloudDef | null;
  veil: VeilDef;
  glow?: GlowDef;
  /** Average albedo of the whole wall (for bounce light). */
  worldAlbedo: RGB;
  /** Builders get the light rig so styles can follow its day/night or glow phase. */
  style: (rig: LightRig) => TerrainStyle;
  grass: (rig: LightRig) => GrassStyle;
  trees: (rig: LightRig) => TreeStyle;
  /**
   * Several biomes on one world (spheres only): how the ground is shared out between them.
   * Styles and plants then read each place's biome weights (SurfaceContext.biome).
   */
  biomes?: BiomeFieldDef;
  /** With biomes: the biome `trees` belong to, and more tree layers, each of one biome. */
  treesBiome?: number;
  biomeTrees?: { biome: number; trees: (rig: LightRig) => TreeStyle }[];
  /** Optional boulder scatter (tree system with rock shapes and its own mask). */
  rocks?: (rig: LightRig) => TreeStyle;
  motes?: (rig: LightRig) => MoteDef;
  birds?: (rig: LightRig) => BirdDef;
  /** Preferred start direction (hollow frame) and the heights a start spot may have. */
  spawn: {
    dir: RGB;
    minHeight: number;
    maxHeight: number /** Direction to face (hollow frame). */;
    face?: RGB;
  };
  /** Ambient soundscape. */
  ambience?: AmbienceDef;
  /** A few first-visit hints, shown one after another. */
  hints?: string[];
  /** Start with the player's lantern lit (worlds without light of their own). */
  lantern?: boolean;
  /** Local clock time at the start point on arrival (hours, default 10). */
  startHour?: number;
  /** Exposure at full daylight; night opens up from here. */
  exposureKey: number;
  /**
   * Tile atlas edge as a fraction of the quality preset's (default 1). A tiny hollow needs
   * far fewer tiles than a big one; half the edge holds a quarter of the tiles.
   */
  atlasScale?: number;
  /**
   * Fade palette noise that's finer than a pixel toward its mean (SurfaceContext.footprint)
   * instead of letting it alias into grain. Titan needs it: its far side is 2,000 km away,
   * where a pixel spans kilometres. Smaller worlds keep their look without it (default off).
   * `?filterPalette=0|1` overrides it for every world.
   */
  filterPalette?: boolean;
  /** Real textures for the ground close by (default on; `?detail=0` turns them off everywhere). */
  detail?: boolean;
  /** Tint of the lakes' sky reflection relative to the ambient light. */
  waterSkyTint: RGB;
}

const _cam = new THREE.Vector3();
const _rel = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _frustum = new THREE.Frustum();
const _up = new THREE.Vector3(0, 1, 0);
const _tmpV = new THREE.Vector3();
const _rc: RootCoord = { root: 0, u: 0, v: 0 };
const _col = new THREE.Color();
const _heroSun = new THREE.Color();
/**
 * A biome's trees grow only where its weight passes 0.3 (their density is at most its weight,
 * and a tree needs 0.3), which is 0.29 or more before faint weights are cut; its layer skips
 * nodes where it can't reach a little less than that.
 */
const TREE_WEIGHT = 0.25;
/** The boulders' random stream (the trees' is 0, each biome's own trees' 1, 2…; see cellRandoms). */
const ROCK_SALT = 9;
/** Nodes whose biomes are remembered, at most (then the memory starts again). */
const NODE_BIOMES_MAX = 50_000;
/** Options for placing a hollow in a larger layout (see CaveWorld). */
export interface HollowSceneOptions {
  /** Universe centre, overriding the def's. */
  center?: THREE.Vector3;
  /** Cave mouths on this hollow's wall. */
  mouths?: TerrainMouth[];
}

/** Lantern brightness (cd, in the renderer's irradiance units: E = I / d²), and its colour. */
export const LANTERN_INTENSITY = 22;
export const LANTERN_COLOR = 0xffd29a;

/**
 * A terrain hollow: GPU terrain on the whole inner surface, a light rig (a sun
 * with a turning veil, or living glow), inverted atmosphere, optional clouds,
 * grass, trees and drifting motes, all configured by a HollowSceneDef.
 */
export class HollowScene implements GameWorld {
  readonly name: string;
  readonly hollows: HollowDef[];
  readonly field = new GravityField();
  readonly hollow: HollowDef;
  get viewRadius(): number {
    return this.shape instanceof TorusShape ? this.shape.tube : this.hollow.radius;
  }
  readonly gen: TileGenerator;
  readonly tree: TerrainQuadtree;
  readonly terrain: TerrainRenderer;
  /** How the ground looks (its palette), shared with the caves that open into it. */
  readonly style: TerrainStyle;
  private collider: TerrainCollider | null = null;
  /** The surface the terrain lies on (the sphere's inside, or the torus's). */
  readonly shape: TerrainShape;
  /** The preferred start direction (hollow frame), and the start spot found near it. */
  private readonly spawnDir: THREE.Vector3;
  private readonly spawnPoint = new THREE.Vector3();
  private readonly spawnNormal = new THREE.Vector3();
  readonly rig: LightRig;
  readonly atmosphere?: Atmosphere;
  readonly clouds?: CloudLayer;
  /** The far wall seen from the centre, which the lakes reflect (sphere hollows). */
  readonly panorama: WorldPanorama | null;
  readonly grass: GrassRenderer;
  readonly trees: TreeRenderer;
  /** The shade of the tree crowns near the camera (null: ?canopy=0). */
  private readonly canopy: CanopyShadow | null;
  /** Boulders and stones (the tree system with rock shapes). */
  readonly rocks: TreeRenderer | null = null;
  readonly motes: Motes | null = null;
  readonly birds: Birds | null = null;
  /** With several biomes: how the ground is shared out, and each biome's own tree layers. */
  readonly biomeField: BiomeField | null = null;
  private readonly treeLayers: { biome: number; renderer: TreeRenderer }[] = [];
  /** Per tree-level node (by key): the biomes that may grow trees on it (bit i: biome i). */
  private readonly nodeBiomes = new Map<number, number>();
  private readonly veil: THREE.Mesh | null = null;
  /** A lantern carried by the player (a point light at the eye, slightly to the side). */
  readonly lantern = new THREE.PointLight(LANTERN_COLOR, 0, 90, 2);
  /** Where the lantern hangs instead, relative to the camera (the hero's hip; see holdLantern). */
  private readonly lanternAt = new THREE.Vector3();
  private lanternHung = false;
  private exposure: number;
  private arrived = false;
  private readonly nightAmbient: number;
  /** Everything this hollow draws (lights stay in the scene; see setActive). */
  readonly group = new THREE.Group();
  /** Light volumes of glowing plants and crystals (drawn by the deferred light pass). */
  readonly lightGroup = new THREE.Group();
  private readonly mouths: TerrainMouth[];
  /** The lights this hollow's surfaces use (its sun, bounce light and the lantern). */
  readonly ownLights: THREE.LightsNode;

  constructor(
    readonly def: HollowSceneDef,
    private readonly origin: RenderOrigin,
    scene: THREE.Scene,
    renderer: THREE.WebGPURenderer,
    opts: HollowSceneOptions = {},
  ) {
    this.name = def.name;
    this.mouths = opts.mouths ?? [];
    scene.add(this.group);
    this.exposure = def.exposureKey;
    this.spawnDir = new THREE.Vector3(...def.spawn.dir).normalize();
    this.hollow = makeHollow({
      id: def.id,
      name: def.name,
      center: opts.center ? [opts.center.x, opts.center.y, opts.center.z] : def.center,
      radius: def.radius,
      seed: def.seed,
      sun: def.sun,
      gravity: def.gravity,
    });
    this.hollows = [this.hollow];
    this.field.add({
      center: this.hollow.center,
      radius: this.hollow.radius,
      strength: this.hollow.gravity,
      tube: def.torus?.tube,
    });
    this.shape = def.torus ? new TorusShape(def.radius, def.torus.tube) : new SphereShape(def.radius);
    // Until chooseSpawn finds gentle ground: the surface in the preferred direction.
    const rc: RootCoord = { root: 0, u: 0, v: 0 };
    this.shape.locate(this.spawnDir.clone().multiplyScalar(def.radius + (def.torus?.tube ?? 0)), rc);
    this.shape.point(rc.root, rc.u, rc.v, this.spawnPoint);
    this.shape.normal(rc.root, rc.u, rc.v, this.spawnNormal);

    const q = quality();
    if (def.biomes && def.biomes.biomes.length > 1 && !def.torus)
      this.biomeField = new BiomeField(def.biomes);

    this.rig = new LightRig(this.hollow, def.veil, def.air, def.glow ?? null);
    this.rig.worldAlbedo.setRGB(...def.worldAlbedo);
    this.rig.update(0);
    const na = def.veil.nightAmbient ?? [0.16, 0.2, 0.34];
    this.nightAmbient = 0.2126 * na[0] + 0.7152 * na[1] + 0.0722 * na[2];
    if (this.rig.sunLight) {
      scene.add(this.rig.sunLight);
      origin.anchor(this.rig.sunLight, this.hollow.center);
    }
    scene.add(this.rig.ambient);
    // Toggled by intensity, not visibility, so switching it never recompiles shaders.
    this.lantern.intensity = def.lantern ? LANTERN_INTENSITY : 0;
    scene.add(this.lantern);
    // This hollow's surfaces see only its own lights: another hollow sharing the scene
    // (joined by a cave) is behind kilometres of rock.
    this.ownLights = lights([
      ...(this.rig.sunLight ? [this.rig.sunLight] : []),
      this.rig.ambient,
      this.lantern,
    ]);
    const useOwnLights = (o: THREE.Object3D) =>
      o.traverse((c) => {
        const m = (c as THREE.Mesh).material as THREE.MeshStandardNodeMaterial | undefined;
        if (m && 'lightsNode' in m) m.lightsNode = this.ownLights;
      });
    if (def.clouds && def.air) {
      const clouds = new CloudLayer(this.hollow, def.clouds, this.rig);
      this.clouds = clouds;
      this.rig.cloudShadow = (local) => clouds.shadowAt(local);
    }
    this.canopy = flags.bool('canopy', true)
      ? new CanopyShadow(renderer, Math.min(def.radius, def.torus?.tube ?? Number.POSITIVE_INFINITY))
      : null;
    const canopy = this.canopy;
    if (canopy) this.rig.canopyShadow = (renderPos) => canopy.sample(renderPos);
    this.rig.attachSunColor();
    if (def.air) this.atmosphere = new Atmosphere(renderer, this.hollow, def.air, this.rig, q.froxels);

    const style = def.style(this.rig);
    this.style = style;
    // The tiles carry the palette's broad noise terms (style.bakedNoise), so the style comes first.
    this.gen = new TileGenerator(
      renderer,
      def.terrain,
      this.shape,
      this.hollow.seed,
      Math.round(q.tileAtlasSize * (def.atlasScale ?? 1)),
      24,
      this.mouths,
      this.biomeField,
      bakeable(
        style.bakedNoise ?? [],
        this.shape.rootArc,
        splitFactorFor(q.terrainQuadPixels),
        2 * (def.radius + (def.torus?.tube ?? 0)),
      ),
    );
    this.tree = new TerrainQuadtree(this.gen, this.shape, heightBound(def.terrain));
    this.tree.splitFactor = splitFactorFor(q.terrainQuadPixels);
    // The far wall, for the lakes to reflect (sphere hollows: a ring's far side isn't one wall).
    this.panorama =
      this.shape instanceof SphereShape && flags.bool('panorama', true)
        ? new WorldPanorama(
            renderer,
            this.gen,
            this.tree,
            this.hollow.radius,
            def.terrain,
            style,
            this.rig,
            this.clouds ?? null,
          )
        : null;
    // Lava lights the ground around it by how much of it there is, and how hard it flares: the
    // panorama's emission, blurred to an eighth of the map (a ninth of the world's radius).
    if (this.panorama && this.rig.pulse === 'chaotic') {
      const panorama = this.panorama;
      this.rig.setLocalGlow((local) => panorama.emissionAt(normalize(local), 3));
    }
    this.terrain = new TerrainRenderer(
      this.gen,
      this.tree,
      this.hollow.center,
      def.terrain,
      style,
      this.ownLights,
      6000,
      this.mouths,
      flags.bool('filterPalette', def.filterPalette ?? false),
      def.detail === false || !flags.bool('detail', true) ? null : terrainDetail(),
      this.panorama,
    );
    this.group.add(this.terrain.mesh);
    origin.anchor(this.terrain.mesh, this.terrain.anchor);

    this.grass = new GrassRenderer(
      this.gen,
      this.tree,
      this.hollow.center,
      this.terrain.anchor,
      def.terrain,
      def.grass(this.rig),
      this.mouths,
    );
    useOwnLights(this.grass.mesh);
    this.group.add(this.grass.mesh);
    origin.anchor(this.grass.mesh, this.terrain.anchor);
    this.trees = new TreeRenderer(
      this.gen,
      this.tree,
      this.hollow.center,
      this.terrain.anchor,
      def.terrain,
      style,
      def.trees(this.rig),
      this.mouths,
      0,
      canopy,
      def.seed,
    );
    useOwnLights(this.trees.group);
    this.group.add(this.trees.group);
    origin.anchor(this.trees.group, this.terrain.anchor);
    this.canopy?.scene.add(this.trees.canopyGroup);
    origin.anchor(this.trees.canopyGroup, this.terrain.anchor);
    this.lightGroup.add(this.trees.lightGroup);
    origin.anchor(this.trees.lightGroup, this.terrain.anchor);
    if (this.biomeField) {
      if (def.treesBiome !== undefined) this.treeLayers.push({ biome: def.treesBiome, renderer: this.trees });
      for (const layer of def.biomeTrees ?? []) {
        const r = new TreeRenderer(
          this.gen,
          this.tree,
          this.hollow.center,
          this.terrain.anchor,
          def.terrain,
          style,
          layer.trees(this.rig),
          this.mouths,
          1 + this.treeLayers.length,
          canopy,
          def.seed,
        );
        useOwnLights(r.group);
        this.group.add(r.group);
        origin.anchor(r.group, this.terrain.anchor);
        this.lightGroup.add(r.lightGroup);
        origin.anchor(r.lightGroup, this.terrain.anchor);
        this.canopy?.scene.add(r.canopyGroup);
        origin.anchor(r.canopyGroup, this.terrain.anchor);
        this.treeLayers.push({ biome: layer.biome, renderer: r });
      }
      for (const l of this.treeLayers) l.renderer.keep = (n) => (this.biomesOn(n) & (1 << l.biome)) !== 0;
    }
    if (def.rocks) {
      this.rocks = new TreeRenderer(
        this.gen,
        this.tree,
        this.hollow.center,
        this.terrain.anchor,
        def.terrain,
        style,
        def.rocks(this.rig),
        this.mouths,
        ROCK_SALT,
        null,
        def.seed,
      );
      useOwnLights(this.rocks.group);
      this.group.add(this.rocks.group);
      this.lightGroup.add(this.rocks.lightGroup);
      origin.anchor(this.rocks.lightGroup, this.terrain.anchor);
      origin.anchor(this.rocks.group, this.terrain.anchor);
    }

    if (def.birds) {
      this.birds = new Birds(def.birds(this.rig));
      this.group.add(this.birds.mesh);
    }
    if (def.motes) {
      this.motes = new Motes(def.motes(this.rig));
      this.group.add(this.motes.mesh);
    }

    const sunDef = def.sun;
    if (sunDef) {
      const sunMesh = new THREE.Mesh(
        new THREE.IcosahedronGeometry(sunDef.radius, 6),
        createSunMaterial(new THREE.Color(...sunDef.color), 60),
      );
      sunMesh.name = 'sun';
      this.group.add(sunMesh);
      origin.anchor(sunMesh, this.hollow.center);

      // The veil: a shade shell around the sun covering the night side of the sky.
      const capAngle = Math.acos(-(1 - 2 * def.veil.litFraction));
      const veilGeo = new THREE.SphereGeometry(sunDef.radius * 1.3, 96, 48, 0, Math.PI * 2, 0, capAngle);
      const veilMat = new THREE.MeshStandardNodeMaterial({
        color: 0x1d1a24,
        roughness: 0.7,
        side: THREE.DoubleSide,
      });
      this.veil = new THREE.Mesh(veilGeo, veilMat);
      this.veil.name = 'veil';
      this.group.add(this.veil);
      origin.anchor(this.veil, this.hollow.center);
    }
  }

  async prepare(): Promise<void> {
    await this.tree.init();
    this.chooseSpawn();
  }

  /**
   * Whether this hollow's data (terrain tiles and their atlas, its air's volumes, the
   * ground's collision) is in memory. Its shell (materials and their compiled shaders,
   * lights, rig, mouths) always is: a world that shares the scene with others (CaveWorld)
   * can be dropped and brought back without building anything.
   */
  loadState: 'loaded' | 'loading' | 'unloaded' = 'loaded';
  private loading: Promise<void> | null = null;
  private loadEpoch = 0;

  /** Compile this hollow's compute shaders in the background (once), so loading it later never stalls. */
  precompile(): Promise<void> {
    // The trees' placement passes compile too, but nothing waits for them (the ground would
    // stream in only after them): a layer simply draws nothing until its pass is ready.
    for (const t of [this.trees, ...this.treeLayers.map((l) => l.renderer), this.rocks]) void t?.precompile();
    // So does the panorama: until it's drawn, the lakes reflect a flat sky colour.
    void this.panorama?.precompile();
    return Promise.all([this.gen.precompile(), this.atmosphere?.precompile()]).then(() => {});
  }

  /** Drop this hollow's data (it must not be the one drawn); load() brings it back. */
  unload(): void {
    if (this.loadState === 'unloaded') return;
    this.loadEpoch++;
    this.loading = null;
    this.tree.reset();
    this.gen.releaseAtlas();
    this.atmosphere?.release();
    this.collider?.reset();
    this.peakTiles = 1;
    this.loadState = 'unloaded';
  }

  /**
   * Bring this hollow's data back: the root tiles first; the rest streams in as the camera
   * needs it, once it's the one drawn. Resolves when the roots are there (or it was
   * dropped again meanwhile).
   */
  load(): Promise<void> {
    if (this.loadState === 'loaded') return Promise.resolve();
    if (this.loading) return this.loading;
    const epoch = ++this.loadEpoch;
    this.loadState = 'loading';
    this.loading = (async () => {
      await this.precompile();
      if (epoch !== this.loadEpoch) return;
      this.gen.restoreAtlas();
      this.atmosphere?.restore();
      await this.tree.init();
      if (epoch !== this.loadEpoch) return;
      this.loadState = 'loaded';
      this.loading = null;
    })();
    return this.loading;
  }

  /** True once the ground under a universe position has collision (and the hollow is loaded). */
  groundReadyAt(p: THREE.Vector3): boolean {
    return this.loadState === 'loaded' && (this.collider?.readyAt(p) ?? true);
  }

  /**
   * Find gentle ground in the preferred direction using the root tiles' heights. A spot
   * scores by its direction from the centre and by its normal: on a torus either alone is
   * ambiguous (the inner and outer walls share directions), together they aren't.
   */
  private chooseSpawn(): void {
    const tex = this.gen.texelMeters(0);
    const { minHeight, maxHeight } = this.def.spawn;
    let best = -1;
    let found: RootCoord | null = null;
    const p = new THREE.Vector3();
    const n = new THREE.Vector3();
    for (const root of this.tree.roots) {
      const h = root.heights;
      if (!h) continue;
      for (let j = 2; j < TILE_RES - 2; j++) {
        for (let i = 2; i < TILE_RES - 2; i++) {
          const v = h[j * TILE_RES + i];
          if (v < minHeight || v > maxHeight) continue;
          const sx = Math.abs(h[j * TILE_RES + i + 1] - h[j * TILE_RES + i - 1]) / (2 * tex);
          const sy = Math.abs(h[(j + 1) * TILE_RES + i] - h[(j - 1) * TILE_RES + i]) / (2 * tex);
          if (sx > 0.12 || sy > 0.12) continue;
          const u = -1 + (2 * i) / (TILE_RES - 1);
          const w = -1 + (2 * j) / (TILE_RES - 1);
          const score =
            this.shape.point(root.face, u, w, p).normalize().dot(this.spawnDir) +
            this.shape.normal(root.face, u, w, n).dot(this.spawnDir);
          if (score > best) {
            best = score;
            found = { root: root.face, u, v: w };
          }
        }
      }
    }
    if (found) {
      this.shape.point(found.root, found.u, found.v, this.spawnPoint);
      this.shape.normal(found.root, found.u, found.v, this.spawnNormal);
    }
  }

  initPhysics(physics: PhysicsWorld): void {
    this.collider = new TerrainCollider(physics, this.tree, this.hollow.center, this.mouths);
  }

  /**
   * Stand the player on the ground: at the start spot, or at `at.dir` (hollow frame)
   * facing the universe point `at.face`.
   */
  async spawn(player: PlayerController, at?: { dir: THREE.Vector3; face: THREE.Vector3 }): Promise<void> {
    const R = this.hollow.radius;
    // Where to stand: the start spot, or (arriving by cave, in a sphere) along at.dir.
    const normal = at ? at.dir.clone().normalize() : this.spawnNormal.clone();
    const point = at ? normal.clone().multiplyScalar(R) : this.spawnPoint.clone();
    // A point above the spot, inside the hollow (in a tiny one, 300 m in would reach past
    // the centre).
    const inset = Math.min(300, (this.def.torus?.tube ?? R) / 2);
    const guess = this.hollow.center.clone().add(point).addScaledVector(normal, -inset);
    // A dropped hollow (a cave world's) first gets its data back.
    await this.load();
    // Load the finest tiles under the spawn point, then stand on them.
    for (let i = 0; i < 200; i++) {
      this.collider!.update(guess);
      await this.tree.pump(64);
      if (this.collider!.readyAt(guess)) break;
      await new Promise((r) => setTimeout(r, 16));
    }
    const h = Math.max(0, this.tree.heightAtLocal(point) || 0);
    const pos = this.hollow.center
      .clone()
      .add(point)
      .addScaledVector(normal, -(h + PLAYER_CENTER_HEIGHT + 0.2));
    player.teleport(pos, normal.clone().negate());
    let facing: THREE.Vector3;
    if (at) facing = at.face;
    else if (this.def.spawn.face) {
      facing = this.hollow.center
        .clone()
        .addScaledVector(new THREE.Vector3(...this.def.spawn.face).normalize(), R);
    } else if (this.shape.kind === 'torus') {
      // Along the ring, where the tube curves up and away.
      const rc: RootCoord = { root: 0, u: 0, v: 0 };
      const along = new THREE.Vector3();
      this.shape.locate(point, rc);
      this.shape.tangents(rc.root, rc.u, rc.v, along, new THREE.Vector3());
      facing = this.hollow.center.clone().add(point).addScaledVector(along, 200);
    } else {
      facing = this.hollow.center
        .clone()
        .addScaledVector(normal.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), 0.3), R);
    }
    player.faceTowards(facing);
    const spawnDir = point.clone().normalize();
    player.pitch = at ? -0.12 : 0.18;
    // First arrival: start at mid-morning local time, so the first look is in daylight.
    if (!this.arrived) {
      this.arrived = true;
      this.rig.setLocalHour(spawnDir, this.def.startHour ?? 10);
    }
  }

  /**
   * Show or hide this hollow when it shares the scene with others (CaveWorld). Lights
   * are dimmed rather than removed, so switching never recompiles shaders. Most materials
   * see only their own hollow's lights (ownLights), but some (thrown stones) see them all.
   */
  setActive(on: boolean): void {
    this.group.visible = on;
    this.lightGroup.visible = on;
    this.rig.dimmer.value = on ? 1 : 0;
    if (!on) this.lantern.intensity = 0;
  }

  /**
   * How much of its full daylight (sun and bounce light) the hollow has at unit direction `dir`
   * (hollow frame): 1 by day, falling at night to the bounce light's night share.
   */
  daylightShare(dir: THREE.Vector3): number {
    return this.shareAt(this.rig.veilAtCpu(dir));
  }

  /** Share of the full daylight where the veil is `v` (1 in a world with no light of its own). */
  private shareAt(v: number): number {
    const sun = this.sunAtGround();
    const bounce = this.bounceAtGround();
    const night = this.nightAmbient;
    const lit = sun * v + bounce * (night + (1 - night) * v);
    return lit > 1e-6 ? lit / (sun + bounce) : 1;
  }

  /** Direct sunlight on the ground at full day (irradiance, mean over RGB). */
  private sunAtGround(): number {
    if (this.rig.sunIrradiance <= 0) return 0;
    const t = this.rig.cpuSunTransmittance(this.hollow.radius);
    return (this.rig.sunIrradiance * (t[0] + t[1] + t[2])) / 3;
  }

  /** Bounce light on the ground at full day (irradiance, mean over RGB). */
  private bounceAtGround(): number {
    const amb = this.rig.ambientIrradiance(_col);
    return (amb.r + amb.g + amb.b) / 3;
  }

  /**
   * How open to the sky the camera is (1 in the hollow … 0 deep in a cave): the air's haze
   * fades out underground, and the motes and birds stay outside.
   */
  setOpen(open: number): void {
    if (this.atmosphere) this.atmosphere.open.value = open;
    if (this.motes) this.motes.mesh.visible = open > 0.4;
    if (this.birds) this.birds.mesh.visible = open > 0.4;
  }

  get lanternLit(): boolean {
    return this.lantern.intensity > 0;
  }

  setLantern(on: boolean): void {
    this.lantern.intensity = on ? LANTERN_INTENSITY : 0;
  }

  holdLantern(at: THREE.Vector3 | null): void {
    this.lanternHung = at !== null;
    if (at) this.lanternAt.copy(at);
  }

  heroLight(p: THREE.Vector3, out: THREE.Color): THREE.Color {
    const dir = _tmpV.subVectors(p, this.hollow.center);
    const r = dir.length();
    dir.divideScalar(Math.max(r, 1e-9));
    this.rig.ambientAtCpu(dir, out);
    // The sunlit ground's bounce, as the rig adds it for the scene (see LightRig.groundBounce):
    // a figure's sides and underside, half of it on average, see the ground around it.
    this.rig.sunAtCpu(dir, r, _heroSun).multiply(this.rig.worldAlbedo).multiplyScalar(0.5);
    return out.add(_heroSun);
  }

  /** The sun on the hero at `p`: `dir` toward it (the hollow's centre), and its irradiance. */
  heroSun(p: THREE.Vector3, dir: THREE.Vector3, out: THREE.Color): THREE.Color {
    dir.subVectors(this.hollow.center, p);
    const r = dir.length();
    dir.divideScalar(Math.max(r, 1e-9));
    return this.rig.sunAtCpu(_tmpV.copy(dir).negate(), r, out);
  }

  /** First arrival by another way than spawn (e.g. up a cave): set the local clock there. */
  arrive(dir: THREE.Vector3): void {
    if (this.arrived) return;
    this.arrived = true;
    this.rig.setLocalHour(dir, this.def.startHour ?? 10);
  }

  /** Start adapting from another hollow's exposure (no jump when switching). */
  adoptExposure(value: number): void {
    this.exposure = value;
  }

  update(player: PlayerController): void {
    if (this.loadState === 'loaded') this.collider?.update(player.position);
  }

  frame(camera: THREE.PerspectiveCamera, cameraUniverse: THREE.Vector3, dt: number): void {
    _cam.subVectors(cameraUniverse, this.hollow.center);
    this.origin.toRender(this.hollow.center, _rel);
    camera.updateMatrixWorld();
    _m.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_m, camera.coordinateSystem, camera.reversedDepth);
    // While its data is still coming back (load() makes the root tiles), nothing is drawn
    // or generated here: the atlas may not be back yet.
    const loaded = this.loadState === 'loaded';
    this.terrain.mesh.visible = loaded;
    if (loaded) {
      this.tree.select(_cam, _frustum, _rel);
      this.terrain.update(cameraUniverse, dt);
      this.grass.update(cameraUniverse, dt);
      this.trees.update(cameraUniverse, dt);
      for (const l of this.treeLayers) {
        if (l.renderer !== this.trees) l.renderer.update(cameraUniverse, dt);
        // (No draw at all for a layer with nothing near.)
        l.renderer.active = l.renderer.count > 0;
      }
      this.rocks?.update(cameraUniverse, dt);
      // The crowns' shade, straight down from the hollow's up at the camera.
      if (this.canopy) {
        this.shape.locate(_cam, _rc);
        this.canopy.render(this.shape.normal(_rc.root, _rc.u, _rc.v, _tmpV).negate());
      }
    }
    this.motes?.update(_cam, dt);
    this.birds?.update(_cam, dt);
    if (loaded) {
      this.collider?.renew();
      void this.tree.pump(quality().tilesPerFrame);
    }

    // Light: advance the day, orient the veil (its cap covers the night side).
    this.rig.update(dt);
    this.clouds?.update(dt);
    this.rig.centerRel.value.copy(_rel);
    if (this.veil) this.veil.quaternion.setFromUnitVectors(_up, _tmpV.copy(this.rig.veilAxis.value).negate());
    // The camera sits at the render origin: hold the lantern low and to the right, unless
    // it hangs at the hero's hip.
    if (this.lanternHung) this.lantern.position.copy(this.lanternAt);
    else this.lantern.position.set(0.35, -0.4, -0.2).applyQuaternion(camera.quaternion);
    // Lakes reflect the far side of the world: roughly the bounce light, tinted by the air.
    const amb = this.rig.ambientIrradiance(_col);
    const tint = this.def.waterSkyTint;
    this.terrain.waterSky.value.setRGB(
      (amb.r / Math.PI) * tint[0],
      (amb.g / Math.PI) * tint[1],
      (amb.b / Math.PI) * tint[2],
    );
    if (this.panorama && loaded) {
      this.panorama.waterSky.value.copy(this.terrain.waterSky.value);
      this.panorama.update(dt);
    }
    this.atmosphere?.update(camera, cameraUniverse);
    this.exposure = this.targetExposure(_cam, dt);
  }

  /**
   * Predictive exposure from how much light reaches the camera: bright in the day,
   * opening up for the dim light of the night side.
   */
  private targetExposure(camLocal: THREE.Vector3, dt: number): number {
    const dir = _tmpV.copy(camLocal).normalize();
    // Adapt only partially: half the sky overhead stays in daylight, so night should
    // read as dim surroundings under a bright far side, not as a brightened day. Where the
    // daylight is mostly direct sun, night falls much further, and the eye follows it further.
    // (A world with no light of its own keeps the key exposure: your lantern is the light.)
    const sun = this.sunAtGround();
    const adapt = 0.2 + 0.25 * (sun > 0 ? sun / (sun + this.bounceAtGround()) : 0);
    const ratio = 1 / this.shareAt(this.rig.veilAtCpu(dir));
    const target = this.def.exposureKey * ratio ** adapt;
    const k = 1 - Math.exp(-dt * 1.2);
    return this.exposure + (target - this.exposure) * k;
  }

  /** The biomes that may grow trees on a tree-level node (bit i: biome i), remembered once its heights are in. */
  private biomesOn(node: TNode): number {
    const known = this.nodeBiomes.get(node.key);
    if (known !== undefined) return known;
    // Until its heights come back, its height range is the whole world's: every biome may be there.
    const bits = this.biomeField!.reachIn(node.dir, node.halfDiag, node.minH, node.maxH, TREE_WEIGHT);
    if (node.heights) {
      if (this.nodeBiomes.size >= NODE_BIOMES_MAX) this.nodeBiomes.clear();
      this.nodeBiomes.set(node.key, bits);
    }
    return bits;
  }
  get lightGroups(): THREE.Object3D[] {
    return [this.lightGroup];
  }

  get hints(): string[] | undefined {
    return this.def.hints;
  }

  get ambience(): AmbienceDef | undefined {
    return this.def.ambience;
  }

  toggleLantern(): void {
    this.lantern.intensity = this.lantern.intensity > 0 ? 0 : LANTERN_INTENSITY;
  }

  get currentExposure(): number {
    return this.exposure;
  }

  describeTime(p: THREE.Vector3): string {
    return this.rig.describe(_tmpV.subVectors(p, this.hollow.center).normalize());
  }

  describeShape(p: THREE.Vector3): { size: string; middle: string } | null {
    const s = this.shape;
    if (!(s instanceof TorusShape)) return null;
    const middle = (s.tube - this.heightAbove(p)) / 1000;
    return {
      size: `ring r ${(s.ring / 1000).toFixed(1)} km, tube r ${(s.tube / 1000).toFixed(1)} km`,
      middle: `${middle.toFixed(2)} km to the tube's middle`,
    };
  }

  hollowAt(p: THREE.Vector3): HollowDef | null {
    return this.shape.heightAbove(_rel.subVectors(p, this.hollow.center)) > -3000 ? this.hollow : null;
  }

  /** How far above its reference surface a point is: altitude for a sphere's `radius − d`. */
  heightAbove(p: THREE.Vector3): number {
    return this.shape.heightAbove(_rel.subVectors(p, this.hollow.center));
  }

  groundAltitude(p: THREE.Vector3): number | null {
    const h = this.tree.heightAtLocal(_rel.subVectors(p, this.hollow.center));
    return Number.isFinite(h) ? Math.max(0, h) : null;
  }

  private peakTiles = 1;

  /** Tiles generated so far over the most tiles known to be needed (monotonic-ish). */
  loadProgress(): number {
    const s = this.tree.stats;
    const done = s.generatedTotal;
    this.peakTiles = Math.max(this.peakTiles, done + s.queued + this.tree.queueSize);
    return Math.min(1, done / this.peakTiles);
  }

  /** True once no terrain tiles are waiting (the view around the camera is complete). */
  settled(): boolean {
    return this.tree.stats.queued === 0 && this.tree.queueSize === 0;
  }

  stats(): Record<string, string> {
    const s = this.tree.stats;
    const detail = this.tree.detail < 1 ? `  detail ${Math.round(this.tree.detail * 100)}%` : '';
    const state = this.loadState === 'loaded' ? '' : `  (${this.loadState})`;
    return {
      terrain: `${s.drawn} nodes  ${s.resident}/${this.gen.maxSlots} tiles  q${s.queued}  gen ${s.generatedTotal}${detail}${state}`,
    };
  }
}

/** Distance factor so one terrain quad covers ~quadPixels on a 1440p, 75° view. */
function splitFactorFor(quadPixels: number): number {
  const pixelAngle = (75 * Math.PI) / 180 / 1440;
  return 1 / (32 * quadPixels * pixelAngle);
}
